import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { AiOutlineFilePdf } from 'react-icons/ai';
import { FaFileMedicalAlt, FaFlask, FaXRay, FaStethoscope, FaBox, FaMapMarkerAlt } from 'react-icons/fa';
import { MdDownload } from 'react-icons/md';

const BookingDetails = () => {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `https://api.elthiumhealth.com/api/admin/booking/${bookingId}`
        );
        if (res.data && res.data.booking) {
          setBooking(res.data.booking);
        } else {
          console.error('Unexpected booking response:', res.data);
        }
      } catch (err) {
        console.error('Failed to fetch booking details:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [bookingId]);

  // ✅ Determine booking type
  const getBookingType = (booking) => {
    if (!booking) return 'unknown';
    
    // First check for package
    if (booking.package && booking.package._id) {
      return 'package';
    }
    
    // Check if it has doctor consultation ID
    if (booking.doctorConsultationBookingId || booking.doctorId) {
      return 'doctor';
    }
    
    // Check for diagnostic booking ID
    if (booking.diagnosticBookingId) {
      // Now check cart items to determine if xray or test
      if (booking.cartItems && booking.cartItems.length > 0) {
        const hasXray = booking.cartItems.some(item => item.type === "xray");
        const hasTest = booking.cartItems.some(item => item.type === "test");
        
        if (hasXray && !hasTest) return 'xray';
        if (hasTest && !hasXray) return 'test';
        if (hasXray && hasTest) return 'diagnostic'; // mixed
      }
      return 'diagnostic'; // default for diagnostic
    }
    
    return 'other';
  };

  // ✅ Get booking badge with icon
  const getBookingBadge = (booking) => {
    const bookingType = getBookingType(booking);
    
    switch(bookingType) {
      case 'package':
        return {
          bg: 'bg-purple-100',
          text: 'text-purple-800',
          border: 'border-purple-500',
          icon: <FaBox className="w-5 h-5 mr-2" />,
          label: 'HEALTH PACKAGE'
        };
      case 'doctor':
        return {
          bg: 'bg-green-100',
          text: 'text-green-800',
          border: 'border-green-500',
          icon: <FaStethoscope className="w-5 h-5 mr-2" />,
          label: 'DOCTOR CONSULTATION'
        };
      case 'xray':
        return {
          bg: 'bg-orange-100',
          text: 'text-orange-800',
          border: 'border-orange-500',
          icon: <FaXRay className="w-5 h-5 mr-2" />,
          label: 'X-RAY/SCAN'
        };
      case 'test':
        return {
          bg: 'bg-blue-100',
          text: 'text-blue-800',
          border: 'border-blue-500',
          icon: <FaFlask className="w-5 h-5 mr-2" />,
          label: 'LAB TEST'
        };
      case 'diagnostic':
      default:
        return {
          bg: 'bg-yellow-100',
          text: 'text-yellow-800',
          border: 'border-yellow-500',
          icon: <FaFlask className="w-5 h-5 mr-2" />,
          label: 'DIAGNOSTIC TEST'
        };
    }
  };

  // ✅ Get item type badge
  const getItemTypeBadge = (item) => {
    if (item.type === "xray") {
      return (
        <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full flex items-center w-fit">
          <FaXRay className="w-3 h-3 mr-1" />
          X-RAY/SCAN
        </span>
      );
    } else if (item.type === "test") {
      return (
        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full flex items-center w-fit">
          <FaFlask className="w-3 h-3 mr-1" />
          LAB TEST
        </span>
      );
    }
    return null;
  };

  // ✅ Format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      const d = new Date(dateString);
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  // ✅ Open Google Maps with address
  const openGoogleMaps = (address) => {
    if (!address) {
      alert("Address not available");
      return;
    }
    
    const encodedAddress = encodeURIComponent(address);
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    window.open(googleMapsUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-xl font-bold text-red-600">Booking not found</h2>
        <p className="text-gray-600 mt-2">Unable to load booking details for ID: {bookingId}</p>
      </div>
    );
  }

  const bookingType = getBookingType(booking);
  const badge = getBookingBadge(booking);
  
  const {
    diagnosticBookingId,
    doctorConsultationBookingId,
    serviceType,
    status,
    date,
    timeSlot,
    totalPrice,
    discount,
    payableAmount,
    transactionId,
    paymentStatus,
    diagnostic,
    staff,
    cartItems = [],
    reportFile,
    diagPrescription,
    address,
    package: bookingPackage,
    doctor,
    patient
  } = booking;

  const fileBase = "https://api.elthiumhealth.com";

  // ✅ Calculate item counts
  const xrayCount = cartItems.filter(item => item.type === "xray").length;
  const testCount = cartItems.filter(item => item.type === "test").length;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header with Back Button */}
        <div className="mb-6">
          <button
            onClick={() => window.history.back()}
            className="flex items-center text-purple-700 hover:text-purple-900 mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>
          
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                Booking Details
              </h2>
              <p className="text-gray-600 mt-1">
                ID: <span className="font-mono font-semibold">{diagnosticBookingId || doctorConsultationBookingId || bookingId}</span>
              </p>
            </div>
            
            {/* Booking Type Badge */}
            <div className={`${badge.bg} ${badge.text} px-4 py-2 rounded-lg flex items-center`}>
              {badge.icon}
              <span className="font-bold">{badge.label}</span>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Booking Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Booking Summary Card */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <svg className="w-6 h-6 mr-2 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 00-1 1v1a1 1 0 002 0V3a1 1 0 00-1-1zM4 4h3a3 3 0 006 0h3a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm2.5 7a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm2.45 4a2.5 2.5 0 10-4.9 0h4.9zM12 9a1 1 0 100 2h3a1 1 0 100-2h-3zm-1 4a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z" clipRule="evenodd"/>
                </svg>
                Booking Summary
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Service Type</p>
                    <p className="font-semibold">
                      {bookingType === 'package' ? 'Health Package' : 
                       bookingType === 'doctor' ? (serviceType === "Online" ? "Online Consultation" : "Clinic Visit") :
                       `${serviceType || 'Diagnostic'} Consultation`}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${status === "Confirmed" ? "bg-green-100 text-green-800" : status === "Cancelled" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"}`}>
                      {status}
                    </span>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Date & Time</p>
                    <p className="font-semibold">
                      {formatDateForDisplay(date)} at {timeSlot}
                    </p>
                  </div>
                  
                  {serviceType && (
                    <div>
                      <p className="text-sm text-gray-500">Delivery Type</p>
                      <p className="font-semibold">{serviceType}</p>
                    </div>
                  )}
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Booking ID</p>
                    <p className="font-semibold font-mono">{diagnosticBookingId || doctorConsultationBookingId || bookingId}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Transaction ID</p>
                    <p className="font-semibold font-mono">{transactionId || 'N/A'}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Payment Status</p>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${paymentStatus === 'captured' ? 'bg-green-100 text-green-800' : paymentStatus === 'failed' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {paymentStatus || 'N/A'}
                    </span>
                  </div>
                  
                  {bookingType === 'package' && bookingPackage && (
                    <div>
                      <p className="text-sm text-gray-500">Package</p>
                      <p className="font-semibold">{bookingPackage.name}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Financial Summary */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="text-lg font-semibold text-gray-800 mb-3">Financial Summary</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Total Price</p>
                    <p className="text-2xl font-bold text-gray-800">₹{totalPrice || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Discount</p>
                    <p className="text-2xl font-bold text-red-600">-₹{discount || 0}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <p className="text-sm text-gray-500">Paid Amount</p>
                    <p className="text-2xl font-bold text-green-700">₹{payableAmount || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Items Booked Card */}
            {bookingType === 'package' ? (
              // Package Details
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                  <FaBox className="w-6 h-6 mr-2 text-purple-600" />
                  Package Details
                </h3>
                
                {bookingPackage ? (
                  <div className="space-y-4">
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h4 className="font-bold text-lg text-purple-800">{bookingPackage.name}</h4>
                      <p className="text-purple-700 mt-1">{bookingPackage.description}</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Total Tests Included</p>
                        <p className="text-2xl font-bold text-blue-600">{bookingPackage.totalTestsIncluded}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Package Price</p>
                        <p className="text-2xl font-bold text-green-700">₹{bookingPackage.price}</p>
                      </div>
                    </div>
                    
                    {bookingPackage.totalTestsIncluded > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Tests Included:</p>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-gray-600">
                            This package includes {bookingPackage.totalTestsIncluded} comprehensive tests covering various health parameters.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No package details available</p>
                )}
              </div>
            ) : cartItems.length > 0 ? (
              // Cart Items Details
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-gray-800 flex items-center">
                    <svg className="w-6 h-6 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"/>
                    </svg>
                    Items Booked ({cartItems.length})
                  </h3>
                  
                  {/* Item Count Badges */}
                  <div className="flex gap-2">
                    {xrayCount > 0 && (
                      <span className="px-3 py-1 bg-orange-100 text-orange-800 text-sm rounded-full flex items-center">
                        <FaXRay className="w-3 h-3 mr-1" />
                        {xrayCount} X-Ray{xrayCount > 1 ? 's' : ''}
                      </span>
                    )}
                    {testCount > 0 && (
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full flex items-center">
                        <FaFlask className="w-3 h-3 mr-1" />
                        {testCount} Test{testCount > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="space-y-4">
                  {cartItems.map((item, idx) => {
                    const itemBadge = getItemTypeBadge(item);
                    return (
                      <div key={idx} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              {itemBadge}
                              <h4 className="ml-2 font-bold text-gray-800 text-lg">
                                {item.title || item.itemDetails?.name || `Item ${idx + 1}`}
                              </h4>
                            </div>
                            
                            {item.itemDetails?.description && (
                              <p className="text-sm text-gray-600 mb-2">{item.itemDetails.description}</p>
                            )}
                            
                            {item.itemDetails?.preparation && (
                              <div className="bg-yellow-50 p-2 rounded border border-yellow-200">
                                <p className="text-xs font-semibold text-yellow-800 mb-1">Preparation Required:</p>
                                <p className="text-xs text-yellow-700">{item.itemDetails.preparation}</p>
                              </div>
                            )}
                          </div>
                          
                          <div className="text-right">
                            <p className="text-2xl font-bold text-green-700">₹{item.totalPrice || item.totalPayable || item.price || 0}</p>
                            {item.offerPrice && item.offerPrice > 0 && (
                              <p className="text-sm text-red-600 line-through">₹{item.price}</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div>
                            <p className="text-gray-500">Quantity</p>
                            <p className="font-semibold">{item.quantity}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Base Price</p>
                            <p className="font-semibold">₹{item.price || 0}</p>
                          </div>
                          {item.offerPrice && item.offerPrice > 0 && (
                            <div>
                              <p className="text-gray-500">Offer Price</p>
                              <p className="font-semibold text-green-600">₹{item.offerPrice}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-gray-500">Total Payable</p>
                            <p className="font-semibold">₹{item.totalPayable || item.price || 0}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Total Summary */}
                <div className="mt-6 pt-6 border-t">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-500">Total Items</p>
                      <p className="text-2xl font-bold text-gray-800">{cartItems.length}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Grand Total</p>
                      <p className="text-3xl font-bold text-green-700">₹{payableAmount || totalPrice || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Files Section */}
            {(reportFile || diagPrescription) && (
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                  <svg className="w-6 h-6 mr-2 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"/>
                  </svg>
                  Reports & Documents
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {reportFile && (
                    <a
                      href={`${fileBase}${reportFile}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-between p-4 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors group"
                      download
                    >
                      <div className="flex items-center">
                        <AiOutlineFilePdf className="w-8 h-8 text-red-500 mr-3" />
                        <div>
                          <p className="font-semibold text-gray-800">Diagnostic Report</p>
                          <p className="text-sm text-gray-500">Click to download</p>
                        </div>
                      </div>
                      <MdDownload className="w-5 h-5 text-blue-600 group-hover:text-blue-800" />
                    </a>
                  )}
                  
                  {diagPrescription && (
                    <a
                      href={`${fileBase}${diagPrescription}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-between p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-colors group"
                      download
                    >
                      <div className="flex items-center">
                        <FaFileMedicalAlt className="w-8 h-8 text-green-500 mr-3" />
                        <div>
                          <p className="font-semibold text-gray-800">Prescription</p>
                          <p className="text-sm text-gray-500">Click to download</p>
                        </div>
                      </div>
                      <MdDownload className="w-5 h-5 text-green-600 group-hover:text-green-800" />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Additional Info */}
          <div className="space-y-6">
            {/* User/Staff Info Card */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <svg className="w-6 h-6 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                </svg>
                User Information
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-blue-700 font-bold text-lg">
                      {staff?.name?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">{staff?.name || 'N/A'}</p>
                    <p className="text-sm text-gray-600">{staff?.email || 'N/A'}</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Employee ID</p>
                    <p className={`font-semibold ${staff?.employeeId ? 'text-blue-700' : 'text-gray-400 italic'}`}>
                      {staff?.employeeId || 'Not assigned'}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Contact Number</p>
                    <p className="font-semibold">{staff?.contact_number || 'N/A'}</p>
                  </div>
                  
                  {patient && patient.name && (
                    <div>
                      <p className="text-sm text-gray-500">Family Member</p>
                      <p className="font-semibold">{patient.name}</p>
                      {patient.relation && (
                        <p className="text-xs text-gray-500">({patient.relation})</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Diagnostic Center Card */}
            {diagnostic && (
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                  <svg className="w-6 h-6 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
                  </svg>
                  Diagnostic Center
                </h3>
                
                <div className="space-y-3">
                  <div>
                    <p className="font-bold text-lg text-gray-800">{diagnostic.name}</p>
                    {diagnostic.description && (
                      <p className="text-sm text-gray-600 mt-1">{diagnostic.description}</p>
                    )}
                  </div>
                  
                  {diagnostic.address && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Address</p>
                      <p 
                        className="text-sm text-blue-700 cursor-pointer hover:text-blue-500 flex items-start"
                        onClick={() => openGoogleMaps(diagnostic.address)}
                      >
                        <FaMapMarkerAlt className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
                        <span>{diagnostic.address}</span>
                      </p>
                    </div>
                  )}
                  
                  {diagnostic.contactPerson?.name && (
                    <div>
                      <p className="text-sm text-gray-500">Contact Person</p>
                      <p className="font-semibold">{diagnostic.contactPerson.name}</p>
                      {diagnostic.contactPerson.contactNumber && (
                        <p className="text-sm text-gray-600">{diagnostic.contactPerson.contactNumber}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Doctor Info Card */}
            {doctor && (
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                  <FaStethoscope className="w-6 h-6 mr-2 text-green-600" />
                  Doctor Details
                </h3>
                
                <div className="space-y-3">
                  <div>
                    <p className="font-bold text-lg text-gray-800">{doctor.name}</p>
                    <p className="text-sm text-gray-600">{doctor.qualification}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Specialization</p>
                    <p className="font-semibold">{doctor.specialization}</p>
                  </div>
                  
                  {doctor.address && serviceType === "Offline" && (
                    <div>
                      <p className="text-sm text-gray-500">Clinic Address</p>
                      <p 
                        className="text-sm text-blue-700 cursor-pointer hover:text-blue-500 flex items-start"
                        onClick={() => openGoogleMaps(doctor.address)}
                      >
                        <FaMapMarkerAlt className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
                        <span>{doctor.address}</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Address Card for Home Collection */}
            {serviceType === "Home Collection" && address && (
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                  <FaMapMarkerAlt className="w-6 h-6 mr-2 text-orange-600" />
                  Collection Address
                </h3>
                
                <div className="space-y-3">
                  <div className="bg-orange-50 p-3 rounded-lg">
                    <p className="text-sm font-semibold text-orange-800 mb-1">Home Collection Address</p>
                    <p className="text-xs text-orange-700">Sample will be collected from this address</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Address Type</p>
                    <p className="font-semibold capitalize">{address.addressType || 'Home'}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Street</p>
                    <p className="font-semibold">{address.street}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-sm text-gray-500">City</p>
                      <p className="font-semibold">{address.city}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">State</p>
                      <p className="font-semibold">{address.state}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-sm text-gray-500">Postal Code</p>
                      <p className="font-semibold">{address.postalCode}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Country</p>
                      <p className="font-semibold">{address.country}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Status Timeline */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Booking Status</h3>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${status === "Confirmed" ? "bg-green-500" : "bg-gray-300"}`}>
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold">Booking Confirmed</p>
                    <p className="text-sm text-gray-500">Booking created successfully</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${status === "Completed" ? "bg-green-500" : status === "Cancelled" ? "bg-red-500" : "bg-gray-300"}`}>
                    {status === "Completed" ? (
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : status === "Cancelled" ? (
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    ) : (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                  <div>
                    <p className="font-semibold">
                      {status === "Completed" ? "Service Completed" : 
                       status === "Cancelled" ? "Booking Cancelled" : "Service Pending"}
                    </p>
                    <p className="text-sm text-gray-500">
                      {status === "Completed" ? "Service has been completed" : 
                       status === "Cancelled" ? "Booking was cancelled" : "Waiting for service completion"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDetails;